import { type PDFDocumentProxy } from 'pdfjs-dist';
import {
  createContext,
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { flushSync } from 'react-dom';

import { type PDFPagesCache } from '../../utils/PDFPagesCache';
import { PDFFindController } from './PDFFindController/PDFFindController';

export type PDFViewerSearchContextType = {
  onSearchPhraseChange: (phrase: string) => void;
  totalResults: number;
  isSearching: boolean;
  clearSearch: () => void;
  pageMatches: number[][];
  pageMatchesLength: number[][];
  currentResultIndex: number;
  setCurrentResultIndex: (index: number) => void;
  getMatchesMapArray: () => MatchesMapArrayType[];
};

const defaultSearchContextValue: PDFViewerSearchContextType = {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  clearSearch: () => {},
  currentResultIndex: -1,
  getMatchesMapArray: () => [],
  isSearching: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onSearchPhraseChange: () => {},
  pageMatches: [],
  pageMatchesLength: [],
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setCurrentResultIndex: () => {},
  totalResults: 0,
};

type MatchesMapArrayType = {
  pageIndex: number;
  divIndex: number;
};

const PDFViewerSearchContext = createContext<PDFViewerSearchContextType>(defaultSearchContextValue);

type PDFViewerSearchContextProviderProps = {
  children: ReactNode;
  pdfDocument: PDFDocumentProxy | null;
  pdfPagesCache: PDFPagesCache | null;
};

export const PDFViewerSearchContextProvider = memo(
  ({ children, pdfDocument, pdfPagesCache }: PDFViewerSearchContextProviderProps) => {
    const findControllerRef = useRef<PDFFindController>();
    const [totalResults, setTotalResults] = useState(0);
    const [isSearching, setIsSearching] = useState(false);
    const [pageMatches, setPageMatches] = useState<number[][]>([]);
    const [pageMatchesLength, setPageMatchesLength] = useState<number[][]>([]);
    const [currentResultIndex, setCurrentResultIndex] = useState(-1);

    const matchesMapArrayRef = useRef<MatchesMapArrayType[]>([]);

    useEffect(() => {
      if (pdfDocument && pdfPagesCache) {
        findControllerRef.current = new PDFFindController(pdfDocument, pdfPagesCache);

        return () => {
          findControllerRef.current?.cleanup();
        };
      }
    }, [pdfDocument, pdfPagesCache]);

    const onSearchPhraseChange = useCallback<PDFViewerSearchContextType['onSearchPhraseChange']>(
      async (phrase: string) => {
        // Return early if PDF is not loaded
        if (!pdfDocument || !pdfPagesCache) {
          return 0;
        }

        flushSync(() => {
          setIsSearching(true);
          setTotalResults(0);
          setCurrentResultIndex(-1);
          setPageMatchesLength([]);
          setPageMatches([]);
        });

        const findController = findControllerRef.current;
        if (!findController) return;
        findController.resetSearch();
        try {
          const { totalResults, pageMatchesLength, pageMatches } = await findController.searchInPdf(phrase);

          matchesMapArrayRef.current.length = 0;
          for (let pageIndex = 0; pageIndex < pageMatches.length; pageIndex++) {
            for (let divIndex = 0; divIndex < pageMatches[pageIndex].length; divIndex++) {
              matchesMapArrayRef.current.push({ divIndex, pageIndex });
            }
          }

          if (totalResults > 0) {
            setCurrentResultIndex(0);
          }
          setTotalResults(totalResults);
          setPageMatchesLength([...pageMatchesLength]);
          setPageMatches([...pageMatches]);

          return totalResults;
        } finally {
          setIsSearching(false);
        }
      },
      [pdfDocument, pdfPagesCache],
    );

    const clearSearch = useCallback<PDFViewerSearchContextType['clearSearch']>(() => {
      setTotalResults(0);
      setCurrentResultIndex(-1);
      setPageMatches([]);
      setPageMatchesLength([]);
      matchesMapArrayRef.current.length = 0;
      findControllerRef.current?.resetSearch();
    }, []);

    const getMatchesMapArray = useCallback<PDFViewerSearchContextType['getMatchesMapArray']>(
      () => matchesMapArrayRef.current,
      [],
    );

    const value = useMemo<PDFViewerSearchContextType>(
      () => ({
        clearSearch,
        currentResultIndex,
        getMatchesMapArray,
        isSearching,
        onSearchPhraseChange,
        pageMatches,
        pageMatchesLength,
        setCurrentResultIndex,
        totalResults,
      }),
      [
        onSearchPhraseChange,
        totalResults,
        isSearching,
        pageMatches,
        pageMatchesLength,
        currentResultIndex,
        getMatchesMapArray,
        clearSearch,
      ],
    );

    return <PDFViewerSearchContext.Provider value={value}>{children}</PDFViewerSearchContext.Provider>;
  },
);

PDFViewerSearchContextProvider.displayName = 'PDFViewerSearchContextProvider';

export const usePDFViewerSearchContext = () => {
  const context = useContext(PDFViewerSearchContext);

  if (!context) {
    throw new Error('usePDFViewerSearchContext must be used within a PDFViewerSearchProvider');
  }

  return context;
};
